import { canonicalTelefone, normalizarTelefone } from '@/lib/telefone';

/**
 * Leitura do relatório de agenda do Careflow (PLANO 8.1).
 *
 * Puro de propósito: recebe os itens de texto COM COORDENADAS que o pdf.js
 * extraiu e devolve as consultas. Nada de PDF, nada de rede — dá para testar
 * com um punhado de objetos, e é o que permite provar as regras chatas (célula
 * que quebra em duas linhas, coluna vazia) sem carregar um arquivo.
 *
 * POR QUE POR COORDENADA, e não por texto: o relatório é uma tabela, e as
 * células quebram em várias linhas. Lido como texto corrido, o sobrenome do
 * paciente aparece entre o horário e o status, e a observação longa engole as
 * colunas seguintes. A posição é o que diz a que coluna cada pedaço pertence.
 */

/**
 * Colunas do relatório, na ordem em que aparecem.
 *
 * `Fone Residencial` e `Procedimentos Vinculados` NÃO são enfeite: sem
 * declará-las, o texto delas seria empurrado para a coluna vizinha e
 * contaminaria o celular e as observações.
 */
export const COLUNAS = [
  { id: 'dentista', rotulo: /^Dentista$/i },
  { id: 'siso', rotulo: /^SISO$/i },
  { id: 'paciente', rotulo: /^Paciente$/i },
  { id: 'horario', rotulo: /^Hor[áa]rio de$/i },
  { id: 'status', rotulo: /^Status$/i },
  { id: 'tipo', rotulo: /^Tipo de Consulta$/i },
  { id: 'foneResidencial', rotulo: /^Fone Residencial$/i },
  { id: 'celular', rotulo: /^Celular$/i },
  { id: 'observacoes', rotulo: /^Observa/i },
  { id: 'procedimentos', rotulo: /^Procedimentos$/i },
];

/** Âncora de registro: toda consulta tem um horário; nem toda tem dentista. */
const HORARIO = /(\d{2}):(\d{2}):(\d{2})/;

/** Duas linhas da mesma célula ficam a ~9pt; linhas de registros, a ~22pt. */
const TOLERANCIA_Y = 3;

const centroDoItem = (i) => i.x + (i.width ?? 0) / 2;

/**
 * Centro de cada coluna, a partir do cabeçalho.
 *
 * O cabeçalho é CENTRALIZADO e os dados são alinhados à esquerda, então a
 * borda esquerda do título não serve de fronteira: o telefone começa 23pt à
 * esquerda da palavra "Celular" e cairia em "Fone Residencial". Comparar
 * CENTRO com CENTRO resolve, e com folga larga — no relatório real a coluna
 * certa fica a ~7pt e a errada a ~68pt.
 */
export function acharColunas(itens) {
  const centros = [];
  for (const col of COLUNAS) {
    const achado = itens.find((i) => col.rotulo.test(i.str.trim()));
    if (achado) centros.push({ id: col.id, centro: centroDoItem(achado) });
  }
  return centros.sort((a, b) => a.centro - b.centro);
}

/** A coluna cujo centro está mais perto do centro do item. */
function colunaDoItem(item, colunas) {
  const c = centroDoItem(item);
  let melhor = null;
  let menor = Infinity;
  for (const col of colunas) {
    const d = Math.abs(c - col.centro);
    if (d < menor) {
      menor = d;
      melhor = col.id;
    }
  }
  return melhor;
}

/**
 * Consultas de UMA página.
 *
 * Cada página do relatório repete o cabeçalho, então as colunas são
 * redescobertas por página — não se assume que a segunda tenha o mesmo layout.
 */
export function lerPagina(itens) {
  const colunas = acharColunas(itens);
  if (colunas.length < 3) return [];

  const limpos = itens
    .map((i) => ({ ...i, str: i.str.trim() }))
    .filter((i) => i.str);

  // Âncoras: os horários, de cima para baixo. No PDF o y cresce para CIMA,
  // então "de cima para baixo" é y decrescente.
  const ancoras = limpos
    .filter((i) => HORARIO.test(i.str) && colunaDoItem(i, colunas) === 'horario')
    .sort((a, b) => b.y - a.y);

  return ancoras.map((ancora, n) => {
    const teto = ancora.y + TOLERANCIA_Y;
    // O último registro vai até o fim da página: a continuação do nome dele
    // pode estar bem abaixo, e não há rodapé para engolir.
    const piso = n + 1 < ancoras.length ? ancoras[n + 1].y + TOLERANCIA_Y : -Infinity;

    const daLinha = limpos
      .filter((i) => i.y <= teto && i.y > piso)
      // Ordem de leitura: de cima para baixo, e da esquerda para a direita —
      // é o que faz "ANDRE LUIZ DE" + "PINA ARAUJO" sair na ordem certa.
      .sort((a, b) => b.y - a.y || a.x - b.x);

    const celulas = {};
    for (const item of daLinha) {
      const col = colunaDoItem(item, colunas);
      celulas[col] = celulas[col] ? `${celulas[col]} ${item.str}` : item.str;
    }
    return celulas;
  });
}

/** "08:00:00 - 08:00:00" → "08:00". Guarda a forma bruta para conferência. */
function extrairHorario(texto) {
  const m = HORARIO.exec(String(texto ?? ''));
  return m ? `${m[1]}:${m[2]}` : '';
}

/**
 * Celular utilizável, ou vazio.
 *
 * A coluna vem como "(62)98312-5801". Só o que tiver cara de celular brasileiro
 * passa: o **fixo não recebe WhatsApp de confirmação**, e tratá-lo como celular
 * criaria uma confirmação que nunca chega — pior que admitir que não dá para
 * confirmar aquela consulta.
 *
 * O `canonicalTelefone` é o mesmo que a dedup de conversas usa: assim o
 * telefone da agenda e o do WhatsApp convergem para a mesma conversa.
 */
export function extrairCelular(texto) {
  const bruto = String(texto ?? '');
  const m = /\(?\d{2}\)?\s*\d{4,5}[-\s]?\d{4}/.exec(bruto);
  if (!m) return '';

  const e164 = canonicalTelefone(normalizarTelefone(m[0]));
  // Celular BR canônico tem 14 caracteres: +55 + DDD + 9 + 8 dígitos.
  return /^\+55[1-9][1-9]9\d{8}$/.test(e164) ? e164 : '';
}

const semAcento = (t) =>
  String(t ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toUpperCase().trim();

/** EM ABERTO é a única que ainda precisa de confirmação. */
export const ehEmAberto = (status) => semAcento(status).includes('EM ABERTO');

/**
 * NÃO COMPARECEU — quem faltou, e é a quem se oferece reagendamento.
 *
 * ⚠ **CANCELADA não entra.** Cancelar é uma decisão que a pessoa tomou e
 * comunicou; faltar é o que deixa a agenda com um buraco e a pessoa sem
 * tratamento. Oferecer reagendamento a quem cancelou soa como insistência.
 *
 * O `semAcento` não é zelo: o relatório escreve "NÃO COMPARECEU", e comparar com
 * a string sem acento falharia. E o status chega da célula por coordenada, então
 * "NÃO" e "COMPARECEU" — que o PDF quebra em duas linhas — já vêm juntos, na
 * mesma coluna. Há teste para as duas coisas.
 */
export const ehNaoCompareceu = (status) => semAcento(status).includes('NAO COMPARECEU');

/**
 * Os dois modos da tela, e o que cada um considera "ativa".
 *
 * A tela faz a mesma coreografia nos dois — ler o PDF, marcar, disparar com
 * cadência —, e o que muda é só QUAIS consultas entram. Concentrar essa
 * diferença aqui evita `if (modo === ...)` espalhado pela página.
 */
export const MODOS = [
  { id: 'confirmacao', rotulo: 'Confirmação', sub: 'consultas em aberto' },
  { id: 'reagendamento', rotulo: 'Reagendamento', sub: 'quem não compareceu' },
];

export const ativaNoModo = (consulta, modo) =>
  (modo === 'reagendamento' ? consulta?.naoCompareceu : consulta?.emAberto) === true;

/**
 * Consultas de todas as páginas, já normalizadas.
 *
 * `paginas` é uma lista de listas de itens `{ str, x, y, width }`.
 *
 * As **Observações NÃO são devolvidas**. É a coluna com conteúdo clínico do
 * paciente, e nada nesta tela precisa dela: carregá-la só a exporia na memória
 * do navegador e no que fosse enviado adiante (PLANO seção 9 — LGPD).
 */
export function lerAgenda(paginas) {
  const consultas = [];

  for (const itens of paginas) {
    for (const c of lerPagina(itens)) {
      const horario = extrairHorario(c.horario);
      if (!horario) continue;

      consultas.push({
        siso: c.siso ?? '',
        paciente: c.paciente ?? '',
        dentista: c.dentista ?? '',
        horario,
        horarioBruto: c.horario ?? '',
        status: c.status ?? '',
        tipo: c.tipo ?? '',
        celular: extrairCelular(c.celular),
        celularBruto: c.celular ?? '',
        emAberto: ehEmAberto(c.status),
        naoCompareceu: ehNaoCompareceu(c.status),
      });
    }
  }

  return consultas;
}

/**
 * Contagens da pré-visualização, para o modo pedido.
 *
 * Os campos são genéricos (`ativas`, e não `emAberto`) porque a mesma contagem
 * serve aos dois modos: no reagendamento "ativa" quer dizer "não compareceu". Um
 * nome preso a um dos modos faria a tela dizer "Em aberto: 12" numa tela de
 * reagendamento.
 */
export function resumir(consultas, modo = 'confirmacao') {
  const ativas = (consultas ?? []).filter((c) => ativaNoModo(c, modo));
  return {
    total: (consultas ?? []).length,
    ativas: ativas.length,
    // Sem celular NÃO significa sem telefone: pode ter só fone residencial,
    // que não recebe WhatsApp. De qualquer forma, não dá para escrever.
    semCelular: (consultas ?? []).filter((c) => !c.celular).length,
    ativasSemCelular: ativas.filter((c) => !c.celular).length,
    confirmaveis: ativas.filter((c) => c.celular).length,
  };
}
